/**
 * Standalone Placement Test Result Page
 * Displays a past test result by session ID
 * Route: /placement-test/result/:sessionId
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import placementTestService, { TestResult, CEFRLevel } from '../services/placementTestService';
import QuestionBreakdownSection from '../components/QuestionBreakdownSection';
import RecommendedCoursesSection from '../components/RecommendedCoursesSection';
import './PlacementTestPage.css';

const getLevelColor = (level: CEFRLevel): string => {
    const colors: Record<CEFRLevel, string> = {
        A1: '#22c55e', A2: '#84cc16', B1: '#eab308',
        B2: '#f97316', C1: '#ef4444', C2: '#dc2626',
    };
    return colors[level];
};

const scoreToCefr = (score: number): CEFRLevel => {
    if (score >= 0.875) return 'C2';
    if (score >= 0.725) return 'C1';
    if (score >= 0.575) return 'B2';
    if (score >= 0.40) return 'B1';
    if (score >= 0.225) return 'A2';
    return 'A1';
};

const DimensionRadar: React.FC<{ scores: Record<string, number>; t: (key: string) => string }> = ({ scores, t }) => {
    const dims = ['reading', 'grammar', 'vocabulary', 'listening', 'speaking'] as const;
    const size = 280;
    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 50;
    const numAxes = dims.length;
    const angleStep = (2 * Math.PI) / numAxes;

    const getPoint = (i: number, val: number) => {
        const angle = angleStep * i - Math.PI / 2;
        return { x: cx + r * val * Math.cos(angle), y: cy + r * val * Math.sin(angle) };
    };

    const rings = [0.25, 0.5, 0.75, 1.0];
    const activeEntries = dims.map((d, i) => ({ dim: d, val: scores[d], i })).filter(e => e.val != null);

    let dataPath = '';
    if (activeEntries.length >= 3) {
        const pts = activeEntries.map(e => getPoint(e.i, e.val));
        dataPath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';
    }

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="dimension-radar-svg" style={{ overflow: 'visible' }}>
            {rings.map((rv, ri) => {
                const pts = dims.map((_, i) => getPoint(i, rv));
                const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';
                return <path key={ri} d={path} fill="none" stroke="#e2e8f0" strokeWidth={rv === 1 ? 1 : 0.5} opacity={0.6} />;
            })}
            {dims.map((_, i) => {
                const ep = getPoint(i, 1);
                return <line key={i} x1={cx} y1={cy} x2={ep.x} y2={ep.y} stroke="#e2e8f0" strokeWidth={0.5} opacity={0.5} />;
            })}
            {dataPath && <path d={dataPath} fill="rgba(102,126,234,0.15)" stroke="#667eea" strokeWidth="2" />}
            {activeEntries.map(e => {
                const p = getPoint(e.i, e.val);
                return <circle key={e.i} cx={p.x} cy={p.y} r={4} fill={getLevelColor(scoreToCefr(e.val))} stroke="#fff" strokeWidth={1.5} />;
            })}
            {dims.map((d, i) => {
                const angle = angleStep * i - Math.PI / 2;
                const lr = r + 26;
                const lx = cx + lr * Math.cos(angle);
                const ly = cy + lr * Math.sin(angle);
                const anchor = Math.abs(Math.cos(angle)) < 0.1 ? 'middle' : Math.cos(angle) > 0 ? 'start' : 'end';
                const hasScore = scores[d] != null;
                return (
                    <text key={i} x={lx} y={ly} textAnchor={anchor} dominantBaseline="central"
                        fontSize={11} fontWeight={500} fill={hasScore ? '#334155' : '#94a3b8'}>
                        {t(`result.dimensions.${d}`)}
                    </text>
                );
            })}
        </svg>
    );
};

const PlacementTestResultPage: React.FC = () => {
    const { sessionId } = useParams<{ sessionId: string }>();
    const { t, i18n } = useTranslation('placementTest');
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [result, setResult] = useState<TestResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showBreakdown, setShowBreakdown] = useState(false);

    useEffect(() => {
        if (!sessionId) return;

        const load = async () => {
            try {
                // Bind session to user if needed (for anonymous tests completed before login)
                try { await placementTestService.bindSession(sessionId); } catch { /* already bound or not needed */ }

                const data = await placementTestService.getFinalResult(sessionId);
                if (data?.result) {
                    setResult(data.result);
                } else {
                    // Fallback to basic result
                    const resultData = await placementTestService.getResult(sessionId);
                    setResult({
                        primaryLevel: resultData.primaryLevel,
                        primaryProbability: resultData.primaryProbability,
                        secondaryLevel: resultData.secondaryLevel,
                        secondaryProbability: resultData.secondaryProbability,
                        confidence: resultData.confidence,
                        confidenceLabel: resultData.confidenceLabel,
                        fullDistribution: resultData.fullDistribution,
                        displayText: resultData.displayText,
                        totalTimeSeconds: resultData.totalTimeSeconds,
                        correctCount: resultData.correctCount,
                        levelDescription: resultData.levelDescription,
                    });
                }
            } catch {
                setError(t('errors.loadFailed'));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [sessionId, t]);

    if (loading) {
        return (
            <div className="placement-test-container">
                <div className="placement-test-card" style={{ textAlign: 'center', padding: '80px 32px' }}>
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    if (error || !result) {
        return (
            <div className="placement-test-container">
                <div className="placement-test-card" style={{ textAlign: 'center', padding: '48px 32px' }}>
                    <p style={{ color: 'var(--color-text-muted)', marginBottom: 24 }}>{error || t('errors.loadFailed')}</p>
                    <button className="primary-btn" onClick={() => navigate('/placement-test/history')}>
                        {t('history.title')}
                    </button>
                </div>
            </div>
        );
    }

    const dimScores = result.dimensionScores || {};
    const dims = ['reading', 'grammar', 'vocabulary', 'listening', 'speaking'] as const;
    const testedDims = dims.filter(d => dimScores[d] != null);

    let strongest = '';
    let weakest = '';
    let maxScore = -1;
    let minScore = 2;
    for (const d of testedDims) {
        const s = dimScores[d]!;
        if (s > maxScore) { maxScore = s; strongest = d; }
        if (s < minScore) { minScore = s; weakest = d; }
    }
    if (maxScore === minScore) {
        strongest = '';
        weakest = '';
    }

    return (
        <div className="placement-test-container">
            <div className="placement-test-card">
                <div className="placement-test-result">
                    {/* Confetti particles */}
                    <div className="result-confetti">
                        <span /><span /><span /><span /><span />
                        <span /><span /><span /><span /><span />
                    </div>

                    <div className="result-header">
                        <p className="result-congrats">{t('result.congrats', '測驗完成！')}</p>
                        <p className="result-congrats-sub">{t('result.congratsSubtext', '以下是你的個人化英語能力報告')}</p>
                    </div>

                    {/* Hero card: badge + description + stats */}
                    <div className="result-hero">
                        <div
                            className="level-badge"
                            style={{ backgroundColor: getLevelColor(result.compositeCefrLevel || result.primaryLevel) }}
                        >
                            <span className="level-text">{result.compositeCefrLevel || result.primaryLevel}</span>
                            <span className="level-name">{result.levelDescription.name}</span>
                        </div>

                        <div className="result-hero-info">
                            <p className="result-display-text">
                                {t('result.levelHeadline', {
                                    level: result.compositeCefrLevel || result.primaryLevel,
                                })}
                            </p>
                            <div className="result-meta">
                                <div className="detail-item">
                                    <span className="detail-label">{t('result.correctCount')}</span>
                                    <span className="detail-value">{result.correctCount} / 10</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">{t('result.timeSpent')}</span>
                                    <span className="detail-value">
                                        {(() => {
                                            const mins = Math.floor(result.totalTimeSeconds / 60);
                                            const secs = result.totalTimeSeconds % 60;
                                            return `${mins}:${String(secs).padStart(2, '0')}`;
                                        })()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="level-description">
                        <h3>{t('result.whatThisMeans')}</h3>
                        <p>{result.levelDescription.description}</p>
                    </div>

                    {/* Dimension Breakdown */}
                    {testedDims.length > 0 && (
                        <div className="dimension-section">
                            <h3>{t('result.dimensionTitle')}</h3>
                            <div className="dimension-layout">
                                <div className="dimension-radar">
                                    <DimensionRadar scores={dimScores} t={t} />
                                </div>
                                <div className="dimension-cards">
                                    {dims.map(d => {
                                        const score = dimScores[d];
                                        const isTested = score != null;
                                        const cefr = isTested ? scoreToCefr(score) : null;
                                        const pct = isTested ? Math.round(score * 100) : 0;
                                        const isStrongest = d === strongest;
                                        const isWeakest = d === weakest && testedDims.length > 1;

                                        return (
                                            <div key={d} className={`dim-card ${!isTested ? 'dim-card--untested' : ''}`}>
                                                <div className="dim-card-header">
                                                    <span className="dim-card-name">{t(`result.dimensions.${d}`)}</span>
                                                    {isStrongest && <span className="dim-card-tag dim-card-tag--strong">{t('result.strongestSkill')}</span>}
                                                    {isWeakest && <span className="dim-card-tag dim-card-tag--weak">{t('result.weakestSkill')}</span>}
                                                </div>
                                                {isTested ? (
                                                    <>
                                                        <div className="dim-card-score">
                                                            <span className="dim-cefr" style={{ color: getLevelColor(cefr!) }}>{cefr}</span>
                                                            <div className="dim-bar-track">
                                                                <div className="dim-bar-fill" style={{ width: `${pct}%`, backgroundColor: getLevelColor(cefr!) }} />
                                                            </div>
                                                        </div>
                                                        {isWeakest && (
                                                            <p className="dim-recommendation">{t(`result.recommendations.${d}`)}</p>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="dim-not-tested">{t('result.dimensionNotTested')}</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Distribution chart */}
                    <div className="distribution-chart">
                        <h3>{t('result.distribution')}</h3>
                        <div className="distribution-bars">
                            {(Object.entries(result.fullDistribution) as [CEFRLevel, number][]).map(([level, prob]) => (
                                <div key={level} className="dist-bar-item">
                                    <div className="dist-bar-wrapper">
                                        <div
                                            className="dist-bar"
                                            style={{
                                                height: `${prob * 100}%`,
                                                backgroundColor: getLevelColor(level),
                                            }}
                                        />
                                    </div>
                                    <span className="dist-label">{level}</span>
                                    <span className="dist-percent">{Math.round(prob * 100)}%</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Question Breakdown */}
                    {result.questionBreakdown && result.questionBreakdown.length > 0 && (
                        <QuestionBreakdownSection
                            breakdown={result.questionBreakdown}
                            showBreakdown={showBreakdown}
                            onToggle={() => setShowBreakdown(!showBreakdown)}
                            t={t}
                            locale={i18n.language}
                        />
                    )}

                    {/* Recommended Courses */}
                    {sessionId && (
                        <RecommendedCoursesSection
                            sessionId={sessionId}
                            primaryLevel={result.compositeCefrLevel || result.primaryLevel}
                            t={t}
                            locale={i18n.language}
                        />
                    )}

                    <div className="result-actions">
                        <button className="primary-btn" onClick={() => navigate('/placement-test')}>
                            {t('result.retakeTest')}
                        </button>
                        {isAuthenticated && (
                            <button className="secondary-btn" onClick={() => navigate('/placement-test/history')}>
                                {t('result.viewHistory')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlacementTestResultPage;
